import type { PhotoCandid } from '@/api/types/photo'
import type { PhotosFilterTriState } from '@/lib/photos/types/photos-filter-tri-state'
import type { PhotosOrientationFilter } from '@/lib/photos/types/photos-orientation-filter'
import type { PhotoOrientation } from './types/photo-orientation'
import type { PhotoTrayFilters } from './types/photo-tray-filters'

/** Filename search replaces album/topic/tag filters; ternary + orientation still apply. */
export function buildTrayFilters(options: {
  isFilenameSearchActive: boolean
  topicTagIds: string[]
  studentIds: string[]
  placed: PhotosFilterTriState
  tagged: PhotosFilterTriState
  favorited: PhotosFilterTriState
  orientation: PhotosOrientationFilter
}): PhotoTrayFilters {
  return {
    topicTagIds: options.isFilenameSearchActive ? [] : options.topicTagIds,
    studentIds: options.isFilenameSearchActive ? [] : options.studentIds,
    placed: options.placed,
    tagged: options.tagged,
    favorited: options.favorited,
    orientation: options.orientation,
  }
}

/** True when the photo has at least one student tag. */
export function isPhotoStudentTagged(photo: PhotoCandid): boolean {
  return (photo.students?.length ?? 0) > 0
}

export function resolvePhotoOrientation(
  photo: Pick<PhotoCandid, 'pixelWidth' | 'pixelHeight'>
): PhotoOrientation | null {
  const width = photo.pixelWidth
  const height = photo.pixelHeight

  if (width == null || height == null || width <= 0 || height <= 0) {
    return null
  }

  if (width === height) return 'square'
  return width > height ? 'landscape' : 'portrait'
}

export function photoMatchesTriState(matchesYes: boolean, state: PhotosFilterTriState): boolean {
  if (state === 'any') return true
  return state === 'yes' ? matchesYes : !matchesYes
}

export function photoMatchesOrientationFilter(
  photo: Pick<PhotoCandid, 'pixelWidth' | 'pixelHeight'>,
  orientation: PhotosOrientationFilter
): boolean {
  if (orientation === 'any') return true

  const resolved = resolvePhotoOrientation(photo)

  if (resolved == null) return false

  return resolved === orientation
}

/**
 * Returns true when the photo has at least one topic tag id in `selectedTopicIds` (OR).
 * An empty set matches every photo (no topic filter active).
 */
export function photoMatchesAnyTopicTag(
  photo: PhotoCandid,
  selectedTopicIds: ReadonlySet<string>
): boolean {
  if (selectedTopicIds.size === 0) return true

  return Boolean(photo.topicTags?.some((tag) => selectedTopicIds.has(tag.topicTagId)))
}

/**
 * Returns true when the photo has at least one student id in `selectedStudentIds` (OR).
 * An empty set matches every photo (no student filter active).
 */
export function photoMatchesAnyStudentTag(
  photo: PhotoCandid,
  selectedStudentIds: ReadonlySet<string>
): boolean {
  if (selectedStudentIds.size === 0) return true

  return Boolean(photo.students?.some((student) => selectedStudentIds.has(student.studentId)))
}

export function filterPhotosForTray(
  photos: PhotoCandid[],
  filters: PhotoTrayFilters
): PhotoCandid[] {
  let result = photos

  if (filters.topicTagIds.length > 0) {
    const selectedTopicIds = new Set(filters.topicTagIds)

    result = result.filter((photo) => photoMatchesAnyTopicTag(photo, selectedTopicIds))
  }

  if (filters.studentIds.length > 0) {
    const selectedStudentIds = new Set(filters.studentIds)

    result = result.filter((photo) => photoMatchesAnyStudentTag(photo, selectedStudentIds))
  }

  if (filters.tagged !== 'any') {
    result = result.filter((photo) =>
      photoMatchesTriState(isPhotoStudentTagged(photo), filters.tagged)
    )
  }

  if (filters.favorited !== 'any') {
    result = result.filter((photo) => photoMatchesTriState(photo.favoritedFlag, filters.favorited))
  }

  if (filters.placed !== 'any') {
    result = result.filter((photo) => photoMatchesTriState(photo.placedFlag, filters.placed))
  }

  if (filters.orientation !== 'any') {
    result = result.filter((photo) => photoMatchesOrientationFilter(photo, filters.orientation))
  }

  return result
}
